/**
 * Windows-1252 (CP1252) Kodierung fuer den DATEV-Export.
 *
 * Warum keine TextEncoder-Loesung: TextEncoder kann ausschliesslich UTF-8.
 * DATEV liest den Buchungsstapel aber als Windows-1252 (ANSI). Ein als UTF-8
 * geschriebenes "Grundstueck Muellerstrasse" kommt in DATEV als "MÃ¼ller" an —
 * und weil der Import nicht scheitert, sondern nur falsch aussieht, faellt das
 * erst dem Steuerberater auf. Deshalb hier eine eigene, feste Tabelle.
 *
 * Warum eine Tabelle und kein Bereichs-Trick: CP1252 ist NICHT Latin-1. Die
 * Positionen 0x80-0x9F sind in Latin-1 unbelegte Steuerzeichen, in CP1252
 * dagegen typografische Zeichen — darunter das Euro-Zeichen (0x80) und die
 * deutschen Anfuehrungszeichen (0x84, 0x93). Genau diese Zeichen kommen in
 * Buchungstexten vor.
 *
 * Umgang mit nicht abbildbaren Zeichen: es gehen nie stillschweigend Bytes
 * verloren. Jedes Zeichen ausserhalb von CP1252 durchlaeuft drei Stufen —
 * feste Transliteration, Diakritika-Abbau, und erst zuletzt das Ersatzzeichen
 * '?'. Was ersetzt wuerde, ist ueber findCp1252Replacements vorab abfragbar,
 * damit die Oberflaeche warnen kann, statt den Anwender vor ein Fragezeichen
 * im festgeschriebenen Beleg zu stellen.
 */

/**
 * Die Zeichen der Positionen 0x80-0x9F, in genau dieser Reihenfolge.
 *
 * Achtung beim Bearbeiten: mehrere dieser Zeichen sind im Editor kaum
 * unterscheidbar (die Anfuehrungsvarianten, En- und Em-Dash). Ein stiller
 * Zeichendreher verschiebt eine ganze Tabellenhaelfte, ohne dass irgendetwas
 * scheitert — nur der Beleg sieht falsch aus. Die Zeilenkommentare nennen
 * deshalb den Byte-Bereich, und der Test prueft einzelne Positionen
 * stichprobenartig gegen ihren Codepoint.
 *
 * U+FFFD markiert die fuenf in CP1252 unbelegten Positionen (0x81, 0x8D,
 * 0x8F, 0x90, 0x9D). Sie sind absichtlich besetzt gehalten, damit der Index
 * der uebrigen Zeichen stimmt; kodiert wird auf sie nie.
 */
const CP1252_HIGH_RANGE =
    '€�‚ƒ„…†‡' + // 0x80-0x87
    'ˆ‰Š‹Œ�Ž�' + // 0x88-0x8F
    '�‘’“”•–—' + // 0x90-0x97
    '˜™š›œ�žŸ'; //  0x98-0x9F

/** Unbelegte Position — weder Ziel noch Quelle einer Abbildung. */
const UNDEFINED_CODE_POINT = 0xfffd;

/**
 * Feste 256er-Tabelle: Byte -> Unicode-Codepoint.
 *
 * 0x00-0x7F und 0xA0-0xFF sind deckungsgleich mit Unicode (ASCII bzw.
 * Latin-1-Obermenge), nur 0x80-0x9F weichen ab. Exportiert, weil die Tests
 * damit zurueckdekodieren — eine Rundreise ist der einzige Beleg dafuer, dass
 * die Tabelle stimmt.
 */
export const CP1252_TO_UNICODE: readonly number[] = Object.freeze(
    Array.from({ length: 256 }, (_unused, byte) =>
        byte >= 0x80 && byte <= 0x9f ? CP1252_HIGH_RANGE.charCodeAt(byte - 0x80) : byte,
    ),
);

/** Umkehrung der Tabelle. Einmal beim Laden gebaut statt je Aufruf gesucht. */
const UNICODE_TO_CP1252 = new Map<number, number>();
CP1252_TO_UNICODE.forEach((codePoint, byte) => {
    if (codePoint === UNDEFINED_CODE_POINT) return;
    UNICODE_TO_CP1252.set(codePoint, byte);
});

/** Ersatzzeichen der letzten Stufe. */
export const CP1252_REPLACEMENT = '?';

/**
 * Feste Ersetzungen fuer Zeichen, die es nicht nach CP1252 schaffen, aber eine
 * eindeutige Entsprechung haben.
 *
 * Bewusst kurz gehalten: hier stehen nur Zeichen, die realistisch aus
 * kopierten Leistungsbeschreibungen, Office-Dokumenten und Web-Formularen in
 * einen Buchungstext geraten. Alles Weitere soll sichtbar zum Fragezeichen
 * werden, statt durch eine geratene Entsprechung ersetzt zu werden.
 *
 * Die deutsche Typografie braucht hier nichts: Umlaute, das scharfe s, das
 * Euro-Zeichen, Paragraf, Grad und die Anfuehrungszeichen sind in CP1252
 * enthalten und werden eine Stufe frueher direkt kodiert.
 */
export const CP1252_TRANSLITERATIONS: Readonly<Record<string, string>> = Object.freeze({
    // Typografie, die Office und Web erzeugen
    '−': '-', // echtes Minuszeichen
    '‑': '-', // geschuetzter Bindestrich
    '‐': '-', // Bindestrich (Unicode-Variante)
    '―': '-', // Horizontalstrich
    '⁄': '/', // Bruchstrich
    'ʼ': "'", // Apostroph (Modifier)
    '′': "'", // Minutenzeichen
    '″': '"', // Sekundenzeichen
    // Unsichtbares. Die Schluessel sind im Quelltext nicht zu sehen — der
    // Zeilenkommentar ist hier die einzige Beschriftung. Sie werden zu nichts
    // aufgeloest statt zu '?': ein Fragezeichen fuer ein unsichtbares Zeichen
    // waere im Beleg schlicht falsch.
    '​': '', // Zero Width Space
    '‌': '', // Zero Width Non-Joiner
    '‍': '', // Zero Width Joiner
    '﻿': '', // Byte Order Mark
    '　': ' ', // ideografisches Leerzeichen
    // Das weiche Trennzeichen U+00AD steht bewusst NICHT hier: es ist in
    // CP1252 auf 0xAD enthalten und wird eine Stufe frueher direkt kodiert.
    // Ein Eintrag hier waere toter Code, der Gegenteiliges suggeriert.
    // Mathematik und Pfeile aus Leistungstexten
    '≠': '!=',
    '≤': '<=',
    '≥': '>=',
    '≈': '~',
    '→': '->',
    '←': '<-',
    '↔': '<->',
    // Aufzaehlungszeichen, die nicht in CP1252 stehen
    '●': '-',
    '▪': '-',
    '‣': '-',
    '⁃': '-',
    // Buchstaben mit Querstrich. Sie sehen aus wie Diakritika, sind aber
    // eigenstaendige Codepoints und zerfallen unter NFD NICHT — der
    // Diakritika-Abbau greift bei ihnen also nicht und sie wuerden ohne diese
    // Eintraege zum Fragezeichen. Betrifft in der Praxis polnische
    // Kundennamen. (Ø und ø brauchen nichts: die stehen in CP1252.)
    'Ł': 'L',
    'ł': 'l',
    'Đ': 'D',
    'đ': 'd',
    'Ħ': 'H',
    'ħ': 'h',
    'ı': 'i', // punktloses i
    // Haeufige Abkuerzungszeichen
    '№': 'Nr.',
    '㎡': 'm2', // Quadratmeter-Ligatur
    '㎥': 'm3', // Kubikmeter-Ligatur
});

/**
 * Baut Diakritika ab: 'ć' -> 'c', 'ș' -> 's', 'ā' -> 'a'.
 *
 * Greift erst NACH der direkten Kodierung, damit deutsche Umlaute nie hier
 * landen — die sind in CP1252 enthalten. Ohne diese Stufe wuerde jeder
 * osteuropaeische Kundenname zu einer Reihe von Fragezeichen.
 */
const stripDiacritics = (character: string): string =>
    character.normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * Kodiert ein einzelnes Zeichen. Gibt die Bytes zurueck, die es ersetzen —
 * meist genau eines, bei Transliterationen mehrere, bei Unsichtbarem keines.
 */
const encodeCharacter = (character: string): number[] => {
    const codePoint = character.codePointAt(0);
    if (codePoint === undefined) return [];

    const direct = UNICODE_TO_CP1252.get(codePoint);
    if (direct !== undefined) return [direct];

    const transliterated = CP1252_TRANSLITERATIONS[character];
    if (transliterated !== undefined) {
        return [...transliterated].flatMap(encodeCharacter);
    }

    const stripped = stripDiacritics(character);
    if (stripped !== character && stripped.length > 0) {
        return [...stripped].flatMap(encodeCharacter);
    }

    // Emoji, kyrillische und asiatische Schrift landen hier. Ein sichtbares
    // Fragezeichen ist besser als ein weggelassenes Zeichen: im Beleg faellt
    // es auf, ein stiller Verlust nicht.
    return [CP1252_REPLACEMENT.charCodeAt(0)];
};

/**
 * Wandelt Text in CP1252-Bytes.
 *
 * NFC-Normalisierung vorab, damit ein als 'a' + kombinierender Trema
 * gespeichertes 'ä' (so liefern es macOS-Dateinamen und manche
 * PDF-Extraktoren) zum einzelnen Codepoint zusammengezogen wird und damit
 * direkt abbildbar ist, statt ueber den Diakritika-Abbau zu 'a' zu verkuemmern.
 */
export const toCp1252Bytes = (text: string): Uint8Array => {
    const normalized = (text ?? '').normalize('NFC');
    const bytes: number[] = [];
    for (const character of normalized) {
        bytes.push(...encodeCharacter(character));
    }
    return Uint8Array.from(bytes);
};

/**
 * Liefert die Zeichen, die beim Kodieren durch '?' ersetzt wuerden — je Zeichen
 * einmal, in der Reihenfolge des ersten Auftretens.
 *
 * Gedacht fuer eine Warnung VOR dem Export. Transliterationen tauchen hier
 * bewusst nicht auf: '≤' wird zu '<=' und bleibt lesbar, ein Emoji dagegen
 * nicht.
 */
export const findCp1252Replacements = (text: string): string[] => {
    const found: string[] = [];
    for (const character of (text ?? '').normalize('NFC')) {
        if (found.includes(character)) continue;
        const codePoint = character.codePointAt(0);
        if (codePoint !== undefined && UNICODE_TO_CP1252.has(codePoint)) continue;
        if (CP1252_TRANSLITERATIONS[character] !== undefined) continue;
        const stripped = stripDiacritics(character);
        if (stripped !== character && stripped.length > 0) continue;
        found.push(character);
    }
    return found;
};
